"use strict";

const BaseModuleSettings = function (
  tool,
  key,
  formKey,
  option,
  iconClass,
  moduleProps,
  initValidatorFunction,
  // Optional { key, name, loadProps, save, onPageReady, isValid } - lets a module keep a
  // slice of its form data backed by a different API/table than ApiCalls.setModuleSettings(),
  // while still sharing this page's single Save/Cancel buttons and form. loadProps()
  // returns props to merge in under `key`; save(data) persists the `key` slice of
  // whatever the user changed, separately from the rest of the form. isValid($page)
  // returning false disables Save, so a module can enforce a rule its own API would
  // otherwise only reject after the user submits. `name` names the slice in the error
  // shown when the module settings save but this slice doesn't.
  extraData,
) {
  var $page;
  var props = { ...moduleProps };
  var initialData;

  var loadListeners = function () {
    const $modal = SettingsModalController.getParentPage();
    $page = $modal.find(`.modal-dynamic-content #${key}-page`);

    Form.initializeAndLoadListeners($page, formKey);

    $page.off("click", ".module-settings-save-button", onSaveButtonClick);
    $page.on("click", ".module-settings-save-button", onSaveButtonClick);

    $page.off("click", ".module-settings-cancel-button", onCancelButtonClick);
    $page.on("click", ".module-settings-cancel-button", onCancelButtonClick);

    $page.off("click", ".module-settings-pdf-preview-button", onPdfPreviewButtonClick);
    $page.on("click", ".module-settings-pdf-preview-button", onPdfPreviewButtonClick);

    $page.off("click", ".module-settings-portal-preview-button", onPortalPreviewButtonClick);
    $page.on("click", ".module-settings-portal-preview-button", onPortalPreviewButtonClick);

    initValidatorFunction(props);

    extraData?.onPageReady?.($page);

    // Namespaced and bound after onPageReady so that a module's own input handlers - which
    // may decide its isValid() answer - have already run by the time checkEnableSaveButton
    // reads it. An un-namespaced off() here would also unbind those handlers.
    $page.off("2N:FormInput.baseModuleSettings");
    $page.on("2N:FormInput.baseModuleSettings", onFormInput);

    checkEnableSaveButton();
  };

  async function getProps() {
    const moduleSettings = await ApiCalls.getModuleSettings();
    initialData = moduleSettings;

    props = $.extend(true, props, { ...moduleSettings });

    props.portalSubscriberTools = ToolSettings.getSetting("group", "portalSubscriberTools");

    if (extraData) {
      props[extraData.key] = await extraData.loadProps();
    }

    return props;
  }

  function getMenuKey() {
    return key;
  }

  function getMenuOption() {
    return option;
  }

  function getMenuIcon() {
    return iconClass;
  }

  function onShow() {
    return;
  }

  function onClose() {
    Form.clearForm(formKey);
  }

  async function resetForm() {
    const menuPath = `modals/settingsModal/${Misc.kebabToCamel(key)}.njk`;

    const html = nunjucks.render(menuPath, {
      menu: { menuOption: getMenuOption() },
      props: await getProps(),
    });
    $page.html(html);

    Form.clearForm(formKey);
    Form.initializeAndLoadListeners($page, formKey);
    checkEnableSaveButton();
  }

  async function confirmDiscardChanges() {
    await MessageModal.showConfirmWarningModalAsPromise(
      "Your changes have not been saved. Discard changes?",
      {
        modalTitle: "Confirm discard changes",
        okMessage: "Confirm",
        returnMessage: "Cancel",
      },
    );
  }

  async function onCancelButtonClick() {
    if (!noUpdatesInForm()) {
      try {
        await confirmDiscardChanges();
        await resetForm();
      } catch (e) {
        // User cancelled, do nothing
        return false;
      }
    }
  }

  function validateUrlInputs() {
    const urlInputs = $page.find("input[type='url']");
    for (const input of urlInputs) {
      const value = $(input).val();
      if (value) {
        if (!isValidUrl(value)) {
          return false;
        }
      }
    }

    return true;
  }

  function isValidUrl(url) {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    } catch (e) {
      return false;
    }
  }

  async function onSaveButtonClick() {
    var isValid = validateUrlInputs();
    if (!isValid) {
      MessageModal.showSimpleWarningModal(
        "One or more URL fields contain invalid URLs. Please enter valid URLs containing http:// or https://",
        {
          modalTitle: "Invalid URL",
          okMessage: "OK",
        },
      );
      return;
    }

    try {
      await MessageModal.showConfirmWarningModalAsPromise(
        "Save Changes? Your changes will be saved for all users. Do you want to save changes? Changes may not be reflected until you refresh the page.",
        {
          modalTitle: "Confirm save changes",
          okMessage: "Confirm",
          returnMessage: "Cancel",
        },
      );
    } catch (e) {
      return false;
    }

    try {
      await Form.getReadyToSavePromise(formKey);
      const formData = Form.getDataFromForm(formKey, false);
      // The extraData slice belongs to a different API/table than module-settings,
      // so it's pulled out and saved separately rather than sent to setModuleSettings.
      let extraFormData;
      if (extraData) {
        extraFormData = formData[extraData.key];
        delete formData[extraData.key];
      }

      // Skipped when the user only touched the extraData slice, so saving that slice
      // doesn't hinge on a request that has nothing left to persist.
      const hasModuleSettingsToSave = formData && Object.keys(formData).length > 0;
      if (hasModuleSettingsToSave) {
        await ApiCalls.setModuleSettings(_getUpdateData(initialData, formData));
      }
      if (extraFormData !== undefined) {
        try {
          await extraData.save(extraFormData);
        } catch (e) {
          // These are separate APIs and tables, so the two saves can't share a transaction.
          // Rather than hide that, say what did persist - only this slice needs the retry,
          // which the still-populated form and enabled Save button allow.
          return UploadValidator.reportError(
            e,
            null,
            hasModuleSettingsToSave
              ? `Your other changes were saved, but ${extraData.name} was not. Press Save to retry.`
              : null,
          );
        }
      }

      Form.clearForm(formKey);
      Form.initializeAndLoadListeners($page, formKey);
      checkEnableSaveButton();
    } catch (e) {
      return UploadValidator.reportError(e);
    }
  }

  function onFormInput(e, value, path) {
    checkEnableSaveButton();
  }

  var _getUpdateData = function (initialData, dataToUpload) {
    return {
      updates: dataToUpload,
    };
  };

  function checkEnableSaveButton() {
    if (noUpdatesInForm() || extraData?.isValid?.($page) === false) {
      disableSaveButton();
    } else {
      enableSaveButton();
    }
  }

  function noUpdatesInForm() {
    const currentFormUpdates = Form.getDataFromForm(formKey, false);
    if (currentFormUpdates) {
      return Object.keys(currentFormUpdates).length === 0;
    }

    return true;
  }

  function disableSaveButton() {
    Misc.toggleDisabled($page.find(".module-settings-save-button"), true);
  }

  function enableSaveButton() {
    Misc.toggleDisabled($page.find(".module-settings-save-button"), false);
  }

  async function onPdfPreviewButtonClick() {
    const previewKey = $(this).data("preview-key");

    return await InspectionPreview.preview(function () {
      const updateData = _getUpdateData(initialData, Form.getDataFromForm(formKey, false));
      updateData.previewKey = previewKey;

      return updateData;
    }, ApiCalls.downloadModuleSettingsPdfPreview);
  }

  async function onPortalPreviewButtonClick() {
    const previewKey = $(this).data("preview-key");
    const updateData = _getUpdateData(initialData, Form.getDataFromForm(formKey, false));
    updateData.previewKey = previewKey;

    if (previewKey === "indcomSelfInspectionPortal") {
      const portalPreviewData = await ApiCalls.getModuleSettingsPortalPreview(updateData);

      IndcomFacilitySelfInspectionModal.showPreview(portalPreviewData);
    } else if (previewKey === "lidNoticePortal") {
      const portalPreviewData = await ApiCalls.getModuleSettingsPortalPreview(updateData);

      LidSelfInspectionModal.showPreview(portalPreviewData);
    }
  }

  return {
    getProps,
    getMenuKey,
    getMenuOption,
    getMenuIcon,
    onClose,
    onShow,
    confirmDiscardChanges,
    resetForm,
    loadListeners,
    noUpdatesInForm,
  };
};

module.exports = BaseModuleSettings;

const ApiCalls = require("../../apiCalls");
const SettingsModalController = require("../settingsModalController");
const MessageModal = require("../../modals/messageModal");
const Misc = require("../../misc");
const Form = require("../../general/form");
const UploadValidator = require("../../general/uploadValidator");
const ToolSettings = require("../toolSettings");
const InspectionPreview = require("../../general/inspectionPreview");
const IndcomFacilitySelfInspectionModal = require("../../portal/indcomFacilitySelfInspection/indcomFacilitySelfInspectionModal");
const LidSelfInspectionModal = require("../../portal/lidSelfInspection/lidSelfInspectionModal");
